import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Select, 
  Typography,
  Row,
  Col,
  Tag,
  message,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminTrophyBadgeApi } from '@/services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const DEFAULT_PAGE_SIZE = 10;

interface TrophyBadgeMasterData {
  id: number;
  name: string;
  description: string;
  iconUrl: string;
  type: 'trophy' | 'badge';
  grade?: number;
  criteriaJson?: string;
  createdAt?: string;
  updatedAt?: string;
}

const AdminTrophyBadgeMasterList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [trophyBadges, setTrophyBadges] = useState<TrophyBadgeMasterData[]>([]);
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [pageInfo, setPageInfo] = useState({ current: initialPage, pageSize: initialPageSize, total: 0 });

  // URL 쿼리 파라미터에서 초기값 가져오기
  const searchType = searchParams.get('type') || 'all';

  const typeOptions = [
    { value: 'all', label: '전체' },
    { value: 'trophy', label: '트로피' },
    { value: 'badge', label: '뱃지' },
  ];

  const syncSearchParams = useCallback((nextType: string, nextPage: number, nextSize: number) => {
    const params = new URLSearchParams();
    if (nextType && nextType !== 'all') params.set('type', nextType);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [setSearchParams]);

  useEffect(() => {
    fetchData(searchType);
  }, [searchType]);

  useEffect(() => {
    syncSearchParams(searchType, pageInfo.current, pageInfo.pageSize);
  }, [searchType, pageInfo.current, pageInfo.pageSize, syncSearchParams]);

  const fetchData = async (type?: string) => {
    setLoading(true);
    try {
      const response = await adminTrophyBadgeApi.listMaster({
        type: type && type !== 'all' ? type : undefined,
      });
      const data = response?.data || response;
      const list = data?.content || [];
      const total = data?.totalElements ?? data?.total ?? list.length;
      setTrophyBadges(list);
      setPageInfo((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('데이터를 불러오는데 실패했습니다.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrophyBadge = () => {
    navigate('/admin/trophy-badge-master/add', { state: { from: `${location.pathname}${location.search}` } });
  };

  const handleEditTrophyBadge = (id: number) => {
    navigate(`/admin/trophy-badge-master/detail/${id}`, { state: { from: `${location.pathname}${location.search}` } });
  };

  const handleTypeChange = (value: string) => {
    // URL 쿼리 파라미터 업데이트 및 페이지 초기화
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ type: value });
    }
    setPageInfo((prev) => ({ ...prev, current: 1 }));
  };

  const handleDelete = async (id: number) => {
    try {
      await adminTrophyBadgeApi.deleteMaster(id);
      message.success('삭제되었습니다.');
      fetchData(searchType);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || '삭제에 실패했습니다.';
      message.error(errorMessage);
      console.error(error);
    }
  };


  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => {
        const offset = (pageInfo.current - 1) * pageInfo.pageSize;
        const totalCount = pageInfo.total || trophyBadges.length;
        const computed = totalCount > 0 ? totalCount - (offset + index) : trophyBadges.length - index;
        return computed > 0 ? computed : 0;
      },
      width: 80,
      align: 'center' as const
    },
    {
      title: '아이콘',
      dataIndex: 'iconUrl',
      key: 'iconUrl',
      width: 80,
      align: 'center' as const,
      render: (iconUrl: string, record: TrophyBadgeMasterData) => (
        <div style={{ 
          width: '48px', 
          height: '48px', 
          border: '2px solid #d9d9d9',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fafafa',
          margin: '0 auto'
        }}>
          {iconUrl ? (
            <img 
              src={iconUrl} 
              alt={record.name}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span style={{ fontSize: '20px', color: '#bfbfbf' }}>
              {record.type === 'trophy' ? '🏆' : '⭐'}
            </span>
          )}
        </div>
      )
    },
    {
      title: '유형',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      align: 'center' as const,
      render: (type: string) => (
        <Tag color={type === 'trophy' ? 'gold' : 'blue'}>
          {type === 'trophy' ? '트로피' : '뱃지'}
        </Tag>
      )
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string) => <strong>{name}</strong>
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      align: 'center' as const
    },
    {
      title: '액션',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: TrophyBadgeMasterData) => (
        <Popconfirm
          title="정말 삭제하시겠습니까?"
          description="수여 이력이 있는 트로피/뱃지는 삭제할 수 없습니다."
          onConfirm={(e) => {
            e?.stopPropagation();
            handleDelete(record.id);
          }}
          onCancel={(e) => e?.stopPropagation()}
          okText="확인"
          cancelText="취소"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
            size="small"
          >
            삭제
          </Button>
        </Popconfirm>
      )
    },
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>트로피 및 뱃지 관리</Title>
          <Text type="secondary">트로피와 뱃지 마스터 데이터를 관리합니다</Text>
        </div>

        {/* 필터 및 액션 영역 */}
        <Row gutter={16} align="bottom" className='mb-4'>
          <Col span={6}>
            <label className="block mb-2 text-sm font-medium">유형</label>
            <Select
              placeholder="유형을 선택하세요"
              value={searchType}
              onChange={handleTypeChange}
              style={{ width: '100%' }}
              size="large"
            >
              {typeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={18} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTrophyBadge}
              size="large"
            >
              트로피/뱃지 추가
            </Button>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={trophyBadges}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleEditTrophyBadge(record.id),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            total: pageInfo.total || trophyBadges.length,
            current: pageInfo.current,
            pageSize: pageInfo.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
          showTotal: (total) => {
            const start = Math.max(total - (pageInfo.current * pageInfo.pageSize) + 1, 1);
            const end = Math.max(total - ((pageInfo.current - 1) * pageInfo.pageSize), 1);
            return `${start}-${end} / 총 ${total}개`;
          }
          }}
        onChange={(pagination) => {
          setPageInfo({
            current: pagination.current || 1,
            pageSize: pagination.pageSize || 10,
            total: pagination.total || pageInfo.total || trophyBadges.length,
          });
        }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTrophyBadgeMasterList;

